import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { useCompany } from '@/hooks/useCompany'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { clientCompanyApi } from '@/api/clientCompany'
import { defaultWorkspace } from '@/utils/workspaceSelection'

interface Props { clientCompanyId: string }

export function CompanyBootstrap({ clientCompanyId }: Props) {
  const location = useLocation()
  const { workspaces, isLoading, companyId, error } = useCompany()
  const { homeClientCompanyId, allWorkspaces } = useAuth()
  const { showError } = useToast()
  const [authOrganizationId, setAuthOrganizationId] = useState<string | null | undefined>(undefined)
  const [hasEnabledAuthConnections, setHasEnabledAuthConnections] = useState<boolean>(false)
  const [loadingAuthSettings, setLoadingAuthSettings] = useState<boolean>(false)
  const [hasHandledError, setHasHandledError] = useState(false)

  // Reset auth settings when company changes
  useEffect(() => {
    setAuthOrganizationId(undefined)
    setHasEnabledAuthConnections(false)
    setLoadingAuthSettings(false)
    setHasHandledError(false)
  }, [clientCompanyId])

  // Handle unauthorized access
  useEffect(() => {
    if (!error || hasHandledError || isLoading) return
    
    // If user doesn't have access to this company, redirect to home
    if (error.includes('do not have access')) {
      setHasHandledError(true)
      showError('Access Denied', 'You do not have access to that company')
      
      // Find home company's first active workspace
      const homeWorkspaces = allWorkspaces?.filter(w => w.ClientCompanyId === homeClientCompanyId) || []
      const homeWorkspace = defaultWorkspace(homeWorkspaces)
      
      if (homeWorkspace && homeClientCompanyId) {
        location.route(`/company/${homeClientCompanyId}/workspace/${homeWorkspace.Id}/dashboard`)
      } else {
        // Fallback to just home company route
        location.route(`/company/${homeClientCompanyId}`)
      }
    }
  }, [error, hasHandledError, isLoading, homeClientCompanyId, allWorkspaces, location, showError])

  // Fetch full company details to check auth setup
  useEffect(() => {
    // Don't fetch if user doesn't have access (error will trigger redirect)
    if (error) return
    
    if (!clientCompanyId || loadingAuthSettings || authOrganizationId !== undefined) return
    
    setLoadingAuthSettings(true)
    clientCompanyApi.getById(clientCompanyId)
      .then(res => {
        if (res.data && !res.error) {
          setAuthOrganizationId(res.data.AuthOrganizationId || null)
          setHasEnabledAuthConnections(res.data.HasEnabledAuthConnections ?? false)
        } else {
          // If fetch fails, assume auth is set up and continue
          setAuthOrganizationId(null)
          setHasEnabledAuthConnections(true)
        }
      })
      .catch(() => {
        // On error, assume auth is set up and continue
        setAuthOrganizationId(null)
        setHasEnabledAuthConnections(true)
      })
      .finally(() => {
        setLoadingAuthSettings(false)
      })
  }, [clientCompanyId, loadingAuthSettings, authOrganizationId, error])

  useEffect(() => {
    if (!clientCompanyId) return
    
    // Wait for loading to complete
    if (isLoading || loadingAuthSettings) return
    
    // Wait for the correct company to be loaded
    if (companyId !== clientCompanyId) return
    
    // Wait for authOrganizationId to be determined (not undefined)
    if (authOrganizationId === undefined) return

    // Redirect to auth settings if no auth org OR no enabled auth connections
    if (authOrganizationId === null || !hasEnabledAuthConnections) {
      location.route(`/company/${clientCompanyId}/settings/authentication`)
      return
    }
    
    if (!workspaces || !workspaces.length) return
    
    const active = defaultWorkspace(workspaces)
    if (active) {
      location.route(`/company/${clientCompanyId}/workspace/${active.Id}/dashboard`)
    }
  }, [clientCompanyId, companyId, workspaces, authOrganizationId, hasEnabledAuthConnections, isLoading, loadingAuthSettings])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mb-4" />
        <p className="text-lg text-slate-600 dark:text-slate-300">Loading...</p>
      </div>
    </div>
  )
}
